import React from 'react';
import { atom, useRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';

export type SiteUpgradeModalStatePlans = {
    tab: 'plans';
    type?: api.SiteType;
    reason: api.SiteUpgradeReason;
};
export type SiteUpgradeModalStateBillingNew = {
    tab: 'billing';
    type: api.SiteType;
    reason: api.SiteUpgradeReason;
};
export type SiteUpgradeModalStateBillingOld = {
    tab: 'billing-old';
    type: api.SiteType;
    plan: api.BillingProduct;
    reason: api.SiteUpgradeReason;
};

export type SiteUpgradeModalState =
    | SiteUpgradeModalStatePlans
    | SiteUpgradeModalStateBillingNew
    | SiteUpgradeModalStateBillingOld;

interface SiteUpgradeModalAPI {
    upgradeState: SiteUpgradeModalState | null;
    site: api.Site | null;
    isOpened: boolean;
    open: (site: api.Site, state: SiteUpgradeModalState) => void;
    close: () => void;
}

/**
 * State for the site upgrade modal.
 */
const siteUpgradeModalStateAtom = atom<SiteUpgradeModalState | null>({
    key: 'siteUpgradeModalState',
    default: null,
});

/**
 * Modal state, separated from `upgradeState` so UI relying on upgradeState is not affected.
 */
const modalVisibilityAtom = atom<api.Site | null>({
    key: 'modalVisibility',
    default: null,
});

export function useSiteUpgradeModal(): SiteUpgradeModalAPI {
    const [upgradeState, setUpgradeState] = useRecoilState(siteUpgradeModalStateAtom);
    const [site, setModalSite] = useRecoilState(modalVisibilityAtom);

    return React.useMemo<SiteUpgradeModalAPI>(
        () => ({
            upgradeState,
            isOpened: Boolean(site),
            site,
            open: (site, newState) => {
                setUpgradeState(newState);
                setModalSite(site);
            },
            close: () => {
                setModalSite(null);
            },
        }),
        [upgradeState, site, setUpgradeState, setModalSite]
    );
}

export function assertIsSiteUpgradeModalNewPricing(
    state: SiteUpgradeModalState
): asserts state is SiteUpgradeModalStateBillingNew {
    if (state.tab !== 'billing') {
        throw new Error('Expected new pricing state');
    }
}
